import { capitalCase } from 'change-case';
import { createSetter, DateRange, UTC } from 'rewire-common';
import { observable, replace } from 'rewire-core';
import { arraySearch, Form, IFormContext, Modal } from 'rewire-ui';
import { IEmployee, IResponseType, ITimecard } from '../DataTypes';
import infoPanelShiftSchedulingViewModel from './InfoPanelShiftSchedulingViewModel';
import responsesLogViewModel, { IResponseInput } from './ResponsesLogViewModel';
import { reasonCategoryDataMapper } from '../utility/DataConverters';
import { getNameSearcher } from '../utility/Searchers';
import { getAdvancedFormAutoComplete } from '../ui/AdvancedAutoComplete';

const levelSearcher = arraySearch(
  ['«none»', 'yes', 'no', 'unavailable'],
  (item: any) => (item && item !== '«none»' && !Array.isArray(item) && capitalCase(item)) || '«none»',
);

const reasonSchema = `
id
name
code
display
other(fields: ["category"])
`;

const workedReasonFilter = {
  field: 'category',
  in: ['worked'],
};

const reasonSearch = getNameSearcher('reasons', () => workedReasonFilter, reasonSchema, reasonCategoryDataMapper);

export class SchedulingCandidatePhoneDialogModel extends Modal {
  form: Form;
  employee?: IEmployee;
  earnedDate?: UTC;

  constructor() {
    super('Candidate Employee Phone');
    this.employee = observable({}) as IEmployee;
    this.form = Form.create(
      (form: IFormContext) => ({
        home: form
          .phone()
          .label('Home')
          .disabled(() => true)
          .accessor((item: any) => item?.phone?.home, createSetter(['phone', 'home'])),
        mobile: form
          .phone()
          .label('Mobile')
          .disabled(() => true)
          .accessor((item: any) => item?.phone?.mobile, createSetter(['phone', 'mobile'])),
        work: form
          .phone()
          .label('Work')
          .disabled(() => true)
          .accessor((item: any) => item?.phone?.work, createSetter(['phone', 'work'])),
        reason: getAdvancedFormAutoComplete(form, reasonSearch, 'Reason').label('Reason'),
        response: form.select(levelSearcher).label('Response'),
        comment: form.string().label('Comment'),
      }),
      {},
    );
    this.action('cancel', this.cancel)
      .action('schedule employee', this.submit, {
        variant: 'contained',
        type: 'submit',
        disabled: () => !this.canSubmit(),
      })
      .action('record response', this.submit, {
        variant: 'contained',
        type: 'submit',
        disabled: () => !this.canSubmit(),
      });
  }

  reset() {
    responsesLogViewModel.reset();
    this.earnedDate = undefined;
    this.form.reset();
  }

  async load(timecards: ITimecard[] | undefined, employee: IEmployee, earnedDate: UTC) {
    this.reset();
    replace(this.employee, employee);
    this.earnedDate = earnedDate;
    this.state.title = `Schedule Employee - ${employee.name} for ${earnedDate.toDateString()}`;
    this.form.value = { phone: employee.phone, reason: timecards?.[0].reason };
    const effective = new DateRange(earnedDate, earnedDate);
    await responsesLogViewModel.loadResponse(timecards, { employeeIds: [employee.id], effective });
    responsesLogViewModel.grid.columns[0].visible = false;
    this.open();
  }

  canSubmit() {
    return infoPanelShiftSchedulingViewModel.canSubmit();
  }

  cancel = () => {
    return true;
  };

  submit = async () => {
    if (this.canSubmit()) {
      const obj = this.form.toObjectValues() as { response: string; comment?: string; reason?: { id: string } };
      const response: IResponseInput | undefined =
        obj.response && obj.response === '«none»' && !obj.comment && !obj.reason
          ? undefined
          : { type: obj.response as IResponseType, comment: obj.comment, reasonId: obj.reason?.id };
      await infoPanelShiftSchedulingViewModel.submit(response);
    }
    return true;
  };
}

const schedulingCandidatePhoneDialogModel = new SchedulingCandidatePhoneDialogModel();
export default schedulingCandidatePhoneDialogModel;
